import { Pod as SharedPod, PodToIncidentTeamId } from '../pods/constants';

/**
 * Scheduled jobs are EventBridge crons.
 *
 * These jobs are often batch execution wrappers over on demand jobs.
 */
export const ScheduledJobs = [
  'dispatchAnthemEnrollmentStatusChecks',
  'dispatchBcbsAlabamaEnrollmentStatusChecks',
  'dispatchBcbsIllinoisEnrollmentStatusChecks',
  'dispatchBcbsMassachusettsEnrollmentStatusChecks',
  'dispatchBcbsMontanaEnrollmentStatusChecks',
  'dispatchBcbsNcEnrollments',
  'dispatchBcbsTexasEnrollmentStatusChecks',
  'dispatchIbcPaEnrollmentStatusChecks',
  'dispatchRegenceIdahoEnrollmentStatusChecks',
  'dispatchRegenceOregonEnrollmentStatusChecks',
  'dispatchRegenceUtahEnrollmentStatusChecks',
  'dispatchUnitedHealthcareEnrollmentStatusChecks',
  'failStalePendingEnrollmentStatusCheckRuns',
  'identifyProviderActions',
  'queueCaqhFormInitialization',
] as const;

/**
 * These jobs are enqueued directly to SQS,
 * either directly from business logic or by batch scheduled job wrappers above.
 */
export const OnDemandJobs = [
  'createBulkProvider',
  'generateScheduledNudgeDraft',
  'patientPersonalizedNudge',
  'bookFollowUpAppointmentsInSignUpSeries',
  'calculateSignUpIntentScore',
  'processSignUpCreateUser',
  'processSignUpAttachStripeCustomer',
  'processSignUpSetProviderRelationship',
  'processSignUpCreateInsuranceEligibility',
  'processSignUpCreateForms',
  'processSignUpInvokeHealthie',
  'processSignUpCreateAuth',
  'processSignUpSendSmsConsentConfirmation',
  'processSignUpSetupAuthZed',
  'processSignUpUpdateMondayRecord',
  'processPostSignUpActions',
  'postSignUpEvaluateCareProgramEnrollment',
  'postSignUpMarkWaitlistFulfilled',
  'postSignUpApplyInfluencerTagToPatient',
  'postSignUpMarkAppointmentHoldAsBooked',
  'postSignUpCreatePatientResponsibilityGuarantee',
  'postSignUpCreatePatientReferralLink',
  'postSignUpTriggerMedicareReferralWorkflow',
  'postSignUpProcessPriorAuthorizationWorkflow',
  'retryWorkBrightEmployeeCreation',
  'initializeCaqhForm',
  'processGoogleCalendarWebhook',
  'processHealthieAppointmentWebhook',
  'processHealthiePatientWebhook',
  'processAppointmentTranscriptWebhook',
  'runWorkflowStep',
  'startWorkflow',
  'processPhotonPrescriptionWebhook',
  'processPhotonOrderWebhook',
  'processJunctionLabtestOrderCreatedWebhook',
  'processJunctionLabtestOrderUpdatedWebhook',
  'processJunctionLabtestAppointmentUpdatedWebhook',
  'processDevelopBenefitVerificationWebhook',
  'processDevelopPriorAuthorizationWebhook',
  'processLearnUponWebhook',
  'processZusObservationEvent',
  'sendAutomatedProviderMessage',
  'generateMealPlanRecipes',
  'generateSingleRecipeDetail',
  'processManagedAgentWebhook',
  'processCxTriageWebhook',
  'processNourishInsightsWebhook',
  'processPayrollReportFile',
  'exampleMigratedJob',
  'processMonthlySessionDurations',
  'evaluateProviderSessionDuration',
] as const;

export const JobVersions = ['v1', 'v2'] as const;

export const Jobs = [...ScheduledJobs, ...OnDemandJobs] as const;

export const Pod = SharedPod;
export type Pod = (typeof Pod)[keyof typeof Pod];
export { PodToIncidentTeamId };

export const Lane = {
  TIME_CRITICAL: 'time_critical',
  STANDARD: 'standard',
  LOW_URGENCY: 'low_urgency',
  INCIDENT: 'incident',
} as const;

export type Lane = (typeof Lane)[keyof typeof Lane];

export interface LaneRuntimeConfig {
  heartbeatIntervalSeconds: number;
  visibilityTimeoutSeconds: number;
}

export const LANE_QUEUE_MAX_RECEIVE_COUNT = 50;

export const LANE_RUNTIME_CONFIG_BY_LANE: Record<Lane, LaneRuntimeConfig> = {
  [Lane.TIME_CRITICAL]: {
    heartbeatIntervalSeconds: 15,
    visibilityTimeoutSeconds: 60,
  },
  [Lane.STANDARD]: {
    heartbeatIntervalSeconds: 30,
    visibilityTimeoutSeconds: 120,
  },
  [Lane.LOW_URGENCY]: {
    heartbeatIntervalSeconds: 60,
    visibilityTimeoutSeconds: 300,
  },
  [Lane.INCIDENT]: {
    heartbeatIntervalSeconds: 15,
    visibilityTimeoutSeconds: 60,
  },
};

/**
 * Platform-owned deliverability SLA per `Lane`, in minutes — how long a message may sit unprocessed
 * before we've broken our promise for that lane. This is a product promise rather than an
 * environment setting, so it is identical across deployment environments.
 *
 * Everything that measures or reacts to deliverability derives from it: the lane pools' autoscaling
 * breakpoints and stuck-queue alarm threshold (`laneQueueDelayScaleOutSteps`/`laneQueueDelayScaleInSteps` and
 * `LaneStack` in `infra/`), and the `lane_job_queue_delay` histogram boundaries
 * (`LANE_QUEUE_DELAY_BUCKETS_MS` in `server/`). Changing a value here moves all of them together.
 */
export const LaneDeliverabilitySlaMinutes: Record<Lane, number> = {
  [Lane.TIME_CRITICAL]: 1,
  [Lane.STANDARD]: 15,
  [Lane.LOW_URGENCY]: 60,
  [Lane.INCIDENT]: 15,
};

/**
 * Fractions of a lane's SLA that its autoscaling policies and alarm break on: scale-in below 0.1,
 * scale-out steps at 0.25/0.5/0.75, stuck-queue alarm at 1.0. Kept beside the SLA so anything
 * measuring SLA burn can line its own resolution up with the points the system actually acts on.
 */
export const LANE_SLA_BURN_FRACTIONS = [0.1, 0.25, 0.5, 0.75, 1] as const;

/**
 * CloudWatch identity of the lane pickup-delay metric: the lane workers publish it
 * and the lane autoscaling policies consume it. It measures first-receive delay
 * in seconds, which is our deliverability SLA.
 */
export const LANE_QUEUE_DELAY_CLOUDWATCH_NAMESPACE = 'Nourish/JobLanes';
export const LANE_QUEUE_DELAY_CLOUDWATCH_METRIC_NAME = 'LaneJobQueueDelay';
export const LANE_QUEUE_DELAY_CLOUDWATCH_DIMENSIONS = {
  ENV: 'Env',
  LANE: 'Lane',
} as const;

/**
 * Webhook headers that can be extracted and forwarded to job handlers.
 */
export enum WebhookHeader {
  CUSTOMER_IO_IDEMPOTENCY_KEY = 'x-cio-idempotency-key',
  GOOGLE_CALENDAR_CHANNEL_ID = 'x-goog-channel-id',
  GOOGLE_CALENDAR_RESOURCE_STATE = 'x-goog-resource-state',
  LEARN_UPON_WEBHOOK_ATTEMPT = 'x-webhook-attempt',
  LEARN_UPON_WEBHOOK_ID = 'x-webhook-id',
  LEARN_UPON_WEBHOOK_TYPE = 'x-webhook-type',
  SLACK_RETRY_NUM = 'x-slack-retry-num',
}
